import moment from 'moment/moment';

import { AddressData } from '../api';
import { ProductOption } from '../containers/WarehouseOperation/WorkSpace/components/SearchResult/SearchResultItem/interfaces';
import {
  BILL_DELIVERY_METHOD,
  BILL_ITEM_PROMOTION_TYPE,
  BILL_PAYMENT_METHOD,
  BILL_PROMOTION_SCOPE,
  BILL_PROMOTION_TYPE,
  DEFAULT_BILL_DELIVERY_METHOD,
  DEFAULT_BILL_PAYMENT_METHOD,
  DEFAULT_ORDER_DELIVERY_METHOD,
  DEFAULT_ORDER_PAYMENT_METHOD,
  ORDER_DELIVERY_METHOD,
  ORDER_PAYMENT_METHOD,
  SummaryBillType,
  SummaryOrderType,
} from './constants';
import { generateUUID } from './function';

interface BigInt {
  /** Convert to BigInt to string form in JSON.stringify */
  toJSON: () => string;
}
// @ts-ignore
// eslint-disable-next-line no-extend-native
BigInt.prototype.toJSON = () => this.toString();

export const SALE_TABS_LOCAL_STORAGE_KEY = 'sale_tabs';

export enum SALE_TAB_TYPE_V1 {
  ORDER,
  BILL,
  RETURN_BILL,
}

export enum DELIVERY_FEE_TYPE {
  DEFAULT = 0,
  MANUAL_SHIPPING_FEE = 1,
  ENTITY_SHIPPING_POLICY_ITEM = 2,
  ENTITY_DISTANCE_SHIPPING_FEE = 3,
  ENTITY_CUSTOM_SHIPPING_FEE = 4,
}

export interface IOrderPromotion {
  id: string;
  name: string;
  rate?: number;
  amount?: number;
}

export interface IBillPromotion {
  id: string;
  name: string;
  rate?: number;
  amount?: number;
  type: BILL_PROMOTION_TYPE;
  scope: BILL_PROMOTION_SCOPE;
  adjustment?: number;
}

export interface IBillItemPromotion {
  id: string;
  name: string;
  rate?: number;
  amount?: number;
  type: BILL_ITEM_PROMOTION_TYPE;
  adjustment?: number;
}

export interface IBillEntityCustomShippingFee {
  id: string;
  billID?: string;
  ownerID: string;
  code?: string;
  name?: string;
  quantity: number;
  fee: number;
  totalFee: number;
}

export interface IDepositMoney {
  cash?: bigint;
  eWallet?: bigint;
  internetBanking?: bigint;
  creditCard?: bigint;
}

export interface IPaidMoney {
  cash?: bigint;
  eWallet?: bigint;
  internetBanking?: bigint;
  creditCard?: bigint;
}

export interface IReturnFee {
  rate?: number;
  amount?: number;
}

export interface ISelectedProduct {
  id: string;
  productID?: string;
  proposalItemID?: string;
  productPricePolicyID?: string;
  productProposalPolicyID?: string;

  code: string;
  name: string;
  avatar: string;

  customerQuantity: number;
  currentPrice: number;
  oldSelectedPriceUnit?: any;
  selectedPriceUnit: any;
  priceUnitList: any[];

  reportQuantity: {
    reportType: number;
    relativeQuantity: number;
    absoluteQuantity: number;
  };
  supplierNote: string;
  customerNote: string;

  action: 'add' | 'update' | 'delete' | 'none';

  soldQuantity: number;
  productOptions: ProductOption[];
}

export interface ISelectedProductForOrder extends ISelectedProduct {
  id: string;
  orderItemID?: string;
}

export interface ISelectedProductForBill extends ISelectedProduct {
  id: string;
  subID?: string;
  billItemID?: string;

  promotions: IBillItemPromotion[];
}

export interface ISelectedProductForReturnBill
  extends Omit<
    ISelectedProduct,
    'customerQuantity' | 'selectedPriceUnit' | 'action'
  > {
  id: string;
  billItemID?: string;

  originPrice?: number;
  customerQuantity?: number;
  returnQuantity?: number;
  selectedPriceUnit?: any;
  action?: 'add' | 'update' | 'delete' | 'none';
}

export interface ISaleTabShortInfo {
  id: string;
  isActive: boolean;
  name: string;
}

export interface ISaleTab {
  id: string;
  isActive: boolean;
  name: string;

  supplier: any;
  customer: any | undefined;

  entityPricePolicyID: string | undefined;
  entityProposalPolicyID: string | undefined;

  addressData: AddressData[];
  addressID: string | undefined;
  currentAddress: AddressData | undefined;

  customerNote: string;
  supplierNote: string;

  bankAccount?: any;
  transportNote?: any; // deliveryNote
  medias?: any[];

  totalCustomerQuantity: number;
}

export interface IOrderSaleTab extends ISaleTab {
  orderID?: string;
  order?: any;
  isWholesaleOrder?: boolean;

  selectedProducts: ISelectedProductForOrder[];
  choiceProductList: ISelectedProductForOrder[];

  paymentMethod: ORDER_PAYMENT_METHOD;
  deliveryMethod: ORDER_DELIVERY_METHOD;

  deliveryFeeType: DELIVERY_FEE_TYPE;
  deliveryFee: number;
  entityShippingPolicyItemID?: string;
  currentEntityShippingPolicyItem?: any;

  promotions: IOrderPromotion[];

  summaryOrder: SummaryOrderType;
}

export interface IBillSaleTab extends ISaleTab {
  billID?: string;
  bill?: any;
  sellerID?: string;
  createdAt: string;

  selectedProducts: ISelectedProductForBill[];
  choiceProductList: ISelectedProductForBill[];

  paymentMethod: BILL_PAYMENT_METHOD;
  deliveryMethod: BILL_DELIVERY_METHOD;

  promotions: IBillPromotion[];

  // phí giao hàng
  customShippingFees?: IBillEntityCustomShippingFee[];

  // tách dòng
  addQuantityValue: number;
  separateLine: boolean;

  summaryBill: SummaryBillType;
  shouldCreateOrder?: boolean;

  totalPrice: bigint;
  depositMoney?: IDepositMoney;
  paidMoney?: IPaidMoney;
  deductedMoney?: bigint;
  excessMoney?: bigint;
  isDebt?: boolean;
  debtMoney?: bigint;
  returnMoney: bigint;
}

export interface IReturnBillSaleTab extends ISaleTab {
  billID?: string;
  bill?: any;
  sellerID?: string;
  createdAt: string;

  selectedProducts: ISelectedProductForReturnBill[];
  choiceProductList: ISelectedProductForReturnBill[];

  paymentMethod: BILL_PAYMENT_METHOD;
  deliveryMethod: BILL_DELIVERY_METHOD;

  deliveryFeeType: DELIVERY_FEE_TYPE;
  deliveryFee: number;
  entityShippingPolicyItemID?: string;
  currentEntityShippingPolicyItem?: any;

  promotions: IBillPromotion[];

  summaryBill: SummaryBillType;

  totalPrice: bigint;
  depositMoney?: IDepositMoney;
  paidMoney?: IPaidMoney;
  deductedMoney?: bigint;
  excessMoney?: bigint;
  isDebt?: boolean;
  debtMoney?: bigint;
  returnMoney: bigint;
  returnFee?: IReturnFee;
}

const getSaleTabs = (): ISaleTab[] =>
  JSON.parse(localStorage.getItem(SALE_TABS_LOCAL_STORAGE_KEY) || '[]');

const saveSaleTabs = (saleTabs: ISaleTab[]): void => {
  localStorage.setItem(SALE_TABS_LOCAL_STORAGE_KEY, JSON.stringify(saleTabs));
};

const genTabName = (type: SALE_TAB_TYPE_V1, index: number) => {
  let tabName = 'Trống';
  const order = index + 1;
  switch (type) {
    case SALE_TAB_TYPE_V1.ORDER: {
      tabName = `Đơn hàng`;
      break;
    }
    case SALE_TAB_TYPE_V1.BILL: {
      tabName = `Hóa đơn`;
      break;
    }
    case SALE_TAB_TYPE_V1.RETURN_BILL: {
      tabName = 'Đổi trả';
      break;
    }
    default:
  }
  return `${tabName} ${order}`;
};

const genNewOrderSaleTab = (index: number, id?: string): IOrderSaleTab => {
  const newTab: IOrderSaleTab = {
    id: id || generateUUID(),
    orderID: undefined,
    order: undefined,
    isActive: true,
    name: genTabName(SALE_TAB_TYPE_V1.ORDER, index),
    isWholesaleOrder: false,

    supplier: undefined,
    customer: undefined,

    entityPricePolicyID: undefined,
    entityProposalPolicyID: undefined,

    addressData: [],
    addressID: undefined,
    currentAddress: undefined,

    customerNote: '',
    supplierNote: '',

    selectedProducts: [],
    choiceProductList: [],

    paymentMethod: DEFAULT_ORDER_PAYMENT_METHOD,
    deliveryMethod: DEFAULT_ORDER_DELIVERY_METHOD,

    deliveryFee: 0,
    deliveryFeeType: DELIVERY_FEE_TYPE.DEFAULT,

    medias: [],

    promotions: [],
    summaryOrder: {
      deliveryFeeType: 0,
      deliveryFee: 0,
      totalPrice: BigInt(0),
      totalTemporaryPrice: BigInt(0),
      totalItem: 0,
      totalAdjustment: BigInt(0),
      adjustments: [],
    },
    totalCustomerQuantity: 0,
  };
  return newTab;
};

const genNewBillSaleTab = (index: number, id?: string): IBillSaleTab => {
  const newTab: IBillSaleTab = {
    id: id || generateUUID(),
    billID: undefined,
    bill: undefined,
    sellerID: undefined,
    createdAt: moment().toISOString(),
    isActive: true,
    name: genTabName(SALE_TAB_TYPE_V1.BILL, index),

    supplier: undefined,
    customer: undefined,

    entityPricePolicyID: undefined,
    entityProposalPolicyID: undefined,

    addressData: [],
    addressID: undefined,
    currentAddress: undefined,

    customerNote: '',
    supplierNote: '',

    selectedProducts: [],
    choiceProductList: [],

    paymentMethod: DEFAULT_BILL_PAYMENT_METHOD,
    deliveryMethod: DEFAULT_BILL_DELIVERY_METHOD,

    promotions: [],
    // phí giao hàng
    customShippingFees: [],
    // tách dòng
    addQuantityValue: 1,
    separateLine: false,

    medias: [],
    summaryBill: {
      debtMoney: BigInt(0),
      deductedMoney: BigInt(0),
      deliveryFeeType: 0,
      depositMoney: undefined,
      excessMoney: BigInt(0),
      isDebt: false,
      isValidBillReturnMoney: false,
      paidMoney: undefined,
      returnMoney: BigInt(0),
      totalDepositMoney: BigInt(0),
      totalPaidMoney: BigInt(0),
      deliveryFee: 0,
      totalPrice: BigInt(0),
      totalTemporaryPrice: BigInt(0),
      totalItem: 0,
      totalAdjustment: BigInt(0),
      adjustments: [],
    },
    totalCustomerQuantity: 0,
    shouldCreateOrder: false,

    totalPrice: BigInt(0),
    returnMoney: BigInt(0),
    paidMoney: {},
    depositMoney: {},
    deductedMoney: BigInt(0),
    excessMoney: BigInt(0),
    isDebt: false,
    debtMoney: BigInt(0),
  };
  return newTab;
};

const genNewReturnBillSaleTab = (
  index: number,
  id?: string
): IReturnBillSaleTab => {
  const newTab: IReturnBillSaleTab = {
    id: id || generateUUID(),
    billID: undefined,
    bill: undefined,
    sellerID: undefined,
    createdAt: moment().toISOString(),
    isActive: true,
    name: genTabName(SALE_TAB_TYPE_V1.RETURN_BILL, index),

    supplier: undefined,
    customer: undefined,

    entityPricePolicyID: undefined,
    entityProposalPolicyID: undefined,

    addressData: [],
    addressID: undefined,
    currentAddress: undefined,

    customerNote: '',
    supplierNote: '',

    selectedProducts: [],
    choiceProductList: [],

    paymentMethod: DEFAULT_BILL_PAYMENT_METHOD,
    deliveryMethod: DEFAULT_BILL_DELIVERY_METHOD,

    deliveryFee: 0,
    deliveryFeeType: DELIVERY_FEE_TYPE.DEFAULT,

    promotions: [],

    medias: [],
    summaryBill: {
      debtMoney: BigInt(0),
      deductedMoney: BigInt(0),
      deliveryFeeType: 0,
      depositMoney: undefined,
      excessMoney: BigInt(0),
      isDebt: false,
      isValidBillReturnMoney: false,
      paidMoney: undefined,
      returnMoney: BigInt(0),
      totalDepositMoney: BigInt(0),
      totalPaidMoney: BigInt(0),
      deliveryFee: 0,
      totalPrice: BigInt(0),
      totalTemporaryPrice: BigInt(0),
      totalItem: 0,
      totalAdjustment: BigInt(0),
      adjustments: [],
    },
    totalCustomerQuantity: 0,

    totalPrice: BigInt(0),
    returnMoney: BigInt(0),
    paidMoney: {},
    depositMoney: {},
    deductedMoney: BigInt(0),
    excessMoney: BigInt(0),
    isDebt: false,
    debtMoney: BigInt(0),
    returnFee: {},
  };
  return newTab;
};

export const appendSaleTab = (
  type: SALE_TAB_TYPE_V1,
  id?: string
): {
  tabs: ISaleTabShortInfo[];
  activeTab: ISaleTab | undefined;
} => {
  const saleTabs = getSaleTabs();
  let activeTab: ISaleTab | undefined;
  const index = saleTabs.length;

  switch (type) {
    case SALE_TAB_TYPE_V1.ORDER: {
      if (id) {
        const foundSaleTab = saleTabs.find(tab => tab.id === id);
        if (foundSaleTab) {
          foundSaleTab.isActive = true;
          activeTab = foundSaleTab;
        } else {
          activeTab = genNewOrderSaleTab(index, id);
          saleTabs.push(activeTab);
        }
      } else {
        activeTab = genNewOrderSaleTab(index);
        saleTabs.push(activeTab);
      }
      break;
    }

    case SALE_TAB_TYPE_V1.BILL: {
      if (id) {
        const foundSaleTab = saleTabs.find(tab => tab.id === id);
        if (foundSaleTab) {
          foundSaleTab.isActive = true;
          activeTab = foundSaleTab;
        } else {
          activeTab = genNewBillSaleTab(index, id);
          saleTabs.push(activeTab);
        }
      } else {
        activeTab = genNewBillSaleTab(index);
        saleTabs.push(activeTab);
      }
      break;
    }

    case SALE_TAB_TYPE_V1.RETURN_BILL: {
      if (id) {
        const foundSaleTab = saleTabs.find(tab => tab.id === id);
        if (foundSaleTab) {
          foundSaleTab.isActive = true;
          activeTab = foundSaleTab;
        } else {
          activeTab = genNewReturnBillSaleTab(index, id);
          saleTabs.push(activeTab);
        }
      } else {
        activeTab = genNewReturnBillSaleTab(index);
        saleTabs.push(activeTab);
      }
      break;
    }
    default:
  }

  const saleTabsWithShortInfo: ISaleTabShortInfo[] = [];
  saleTabs.forEach(tab => {
    tab.isActive = activeTab ? tab.id == activeTab.id : false;
    saleTabsWithShortInfo.push({
      id: tab.id,
      isActive: tab.isActive,
      name: tab.name,
    });
  });

  saveSaleTabs(saleTabs);

  return { tabs: saleTabsWithShortInfo, activeTab };
};

export const updateSaleTab = (id: string, data: ISaleTab) => {
  const saleTabs = getSaleTabs();
  const foundSaleTabIdx = saleTabs.findIndex(tab => tab.id == id);
  if (foundSaleTabIdx != -1) {
    const updatedSaleTab: ISaleTab = {
      ...saleTabs[foundSaleTabIdx],
      ...data,
    };
    saleTabs.splice(foundSaleTabIdx, 1, updatedSaleTab);
  }
  saleTabs.forEach(tab => {
    tab.isActive = tab.id == id;
  });
  saveSaleTabs(saleTabs);
};

export const deleteSaleTab = (id: string) => {
  const saleTabs = getSaleTabs();
  const foundSaleTabIdx = saleTabs.findIndex(tab => tab.id == id);
  if (foundSaleTabIdx != -1) {
    const deletedSaleTab = saleTabs[foundSaleTabIdx];
    saleTabs.splice(foundSaleTabIdx, 1);
    if (deletedSaleTab.isActive && saleTabs.length) {
      saleTabs[saleTabs.length].isActive = true;
    }
  }
  saveSaleTabs(saleTabs);
};

export const resetSaleTabs = () => {
  saveSaleTabs([]);
};

export const loadSaleTabs = (): {
  tabs: ISaleTabShortInfo[];
  activeTab: ISaleTab | undefined;
} => {
  const saleTabs = getSaleTabs();
  const activeTab = saleTabs.find(tab => tab.isActive);
  const saleTabsWithShortInfo: ISaleTabShortInfo[] = saleTabs.map(tab => {
    return { id: tab.id, isActive: tab.isActive, name: tab.name };
  });
  return { tabs: saleTabsWithShortInfo, activeTab };
};

export const loadSaleTab = (id: string): ISaleTab | undefined => {
  const saleTabs = getSaleTabs();
  const foundTab = saleTabs.find(tab => tab.id == id);
  if (foundTab) {
    foundTab.isActive = true;
    saleTabs.forEach(tab => {
      tab.isActive = tab.id == id;
    });
    saveSaleTabs(saleTabs);
  }
  return foundTab;
};
